// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useEffect } from 'react';
import { Link } from '@/i18n/router';
import { useWishlist } from '@/hooks/useWishlist';
import { useCart } from '@/hooks/useCart';
import ProductImage from '../base/ProductImage';

const WishlistDrawer = () => {
  useStoreLanguage();
  const {
    wishlistItems,
    removeFromWishlist,
    clearWishlist,
    totalWishlistItems,
    isWishlistOpen,
    setIsWishlistOpen,
  } = useWishlist();
  const { addToCart } = useCart();

  useEffect(() => {
    if (isWishlistOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isWishlistOpen]);

  if (!isWishlistOpen) return null;

  const handleAddToCartAndRemove = (item: typeof wishlistItems[0]) => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
    });
    removeFromWishlist(item.id);
  };

  const handleMoveAllToCart = () => {
    wishlistItems.forEach((item) => {
      addToCart({
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image,
      });
    });
    clearWishlist();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={() => setIsWishlistOpen(false)}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col animate-wishlist-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-xl text-rose-500"></i>
            </div>
            <h2 className="font-playfair text-xl font-semibold text-gray-900">{tr("Lista de Deseos")}</h2>
            {tr(totalWishlistItems > 0 && (
              <span className="bg-rose-100 text-rose-600 text-sm font-medium px-2 py-0.5 rounded-full">
                {tr(totalWishlistItems)}
              </span>
            ))}
          </div>
          <button
            onClick={() => setIsWishlistOpen(false)}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Content */}
        {tr(wishlistItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
            <div className="w-24 h-24 flex items-center justify-center bg-rose-50 rounded-full mb-6">
              <i className="ri-heart-line text-4xl text-rose-300"></i>
            </div>
            <h3 className="font-playfair text-lg font-semibold text-gray-900 mb-2">{tr("Tu lista está vacía")}</h3>
            <p className="text-gray-500 text-sm text-center mb-6">{tr("Guarda tus productos favoritos para comprarlos más tarde")}</p>
            <Link
              to="/products"
              onClick={() => setIsWishlistOpen(false)}
              className="bg-forest-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-forest-700 transition-colors cursor-pointer whitespace-nowrap"
            >{tr("Explorar Productos")}</Link>
          </div>
        ) : (
          <>
            {/* Items List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {tr(wishlistItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 bg-gray-50 rounded-xl p-4 group/item"
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                      <ProductImage
                        src={item.image}
                        alt={tr(item.name)}
                        productName={item.name}
                        category={item.category}
                        className="w-full h-full object-cover object-top"
                        fallbackClassName="w-full h-full"
                        lazy={false}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-inter font-medium text-gray-900 text-sm truncate">
                        {tr(item.name)}
                      </h4>
                      {tr(item.category && (
                        <span className="inline-block text-xs text-forest-600 bg-forest-50 px-2 py-0.5 rounded-full mt-1">
                          {tr(item.category)}
                        </span>
                      ))}
                      <p className="text-forest-600 font-semibold text-sm mt-1">
                        {tr(item.price)} &euro;
                      </p>
                      <button
                        onClick={() => handleAddToCartAndRemove(item)}
                        className="mt-2 flex items-center gap-1.5 text-xs font-medium text-forest-600 hover:text-forest-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-shopping-cart-line text-sm"></i>{tr("Mover al carrito")}</button>
                    </div>
                    <button
                      onClick={() => removeFromWishlist(item.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-colors cursor-pointer self-start"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                )))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-white space-y-3">
              <button
                onClick={handleMoveAllToCart}
                className="w-full bg-forest-600 text-white py-3.5 rounded-lg font-medium hover:bg-forest-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-cart-line"></i>{tr("Añadir Todo al Carrito")}</button>
              <button
                onClick={clearWishlist}
                className="w-full text-gray-500 text-sm hover:text-rose-500 transition-colors cursor-pointer py-2"
              >{tr("Vaciar lista de deseos")}</button>
            </div>
          </>
        ))}
      </div>

      <style>{`
        @keyframes wishlistSlideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-wishlist-slide-in {
          animation: wishlistSlideIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default WishlistDrawer;
